import React, { useEffect, useState, useRef } from 'react';
import {
  Table,
  Alert,
  Button,
  Input,
  Icon
} from 'rsuite';
import axios from 'axios';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useDebounce from '../../../hooks/useDebounce';

import Page from '../../../components/Page';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';

const DraggableCell = ({ children, onDrag, id, rowData, ...rest }) => {
  const ref = useRef(null);
  const [{ canDrop, isOver }, drop] = useDrop({
    accept: 'row',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    }),
    drop(item, monitor) {
      onDrag && onDrag(item.id, rowData.id);
    }
  });
  const [{ isDragging }, drag] = useDrag({
    item: { id: rowData.id, type: 'row' },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  const isActive = canDrop && isOver;

  drag(drop(ref));

  return (
    <Table.Cell {...rest} style={{ padding: '1rem', cursor: 'move' }}>
      <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1, background: isActive ? "#ddd" : null }}>
        {children}
      </div>
    </Table.Cell>
  )
};

const Picture = () => {
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 500);

  const getArticles = () => {
    axios.get(`/admin/boards/pictures?query=${debouncedQuery}&sort=home`)
    .then(res => res.data)
    .then(res => {
      setArticles(res.pictures);
      setLoading(false);
    })
    .catch(err => {
      Alert.error("데이터를 가져오던 중 오류가 발생했습니다!");
      setLoading(false);
    });
  };

  const removeArticle = (id) => {
    if (window.confirm("삭제하시겠습니까?")) {
      axios.delete(`/admin/boards/pictures/${id}`)
      .then(() => {
        Alert.info('삭제되었습니다!');
        getArticles();
      })
      .catch(err => {
        Alert.error(err.response.data.message);
      });
    }
  };

  const exportToExcel = () => {
    axios(`/admin/boards/pictures/export`, {
      responseType: 'blob'
    })
    .then(res => {
      fileDownload(res.data, '[바네스데코] 사진 게시글 리스트.xlsx');
    });
  };

  const toggleArticle = (id) => {
    axios.put(`/admin/boards/pictures/status/${id}`)
    .then(() => {
      getArticles();
    })
    .catch(err => {
      Alert.error("오류가 발생했습니다!");
    });
  };

  const sort = async (source, sourceId, targetId) => {
    const nextData = source.filter((item) => item.id !== sourceId);
    const dragItem = source.find((item) => item.id === sourceId);
    const index = nextData.findIndex((item) => item.id === targetId);
  
    nextData.splice(index + 1, 0 ,dragItem);
    await changeSort(sourceId, targetId);
  
    return nextData;
  };
  
  const changeSort = async (source, target) => {
    setLoading(true);

    await axios({
      method: 'PUT',
      url: '/admin/boards/pictures/homeSort',
      data: {
        target,
        source
      }
    });

    getArticles();
  }

  const handleDragRow = (sourceId, targetId) => {
    sort(articles, sourceId, targetId);
  };

  useEffect(() => {
    getArticles();
  }, [debouncedQuery]);

  return (
    <Page pageName="pictureHomeSort">
      <h3>사진 게시글 메인 정렬 관리</h3>
      <Input type="text" placeholder="검색어를 입력해주세요" style={{ width: '20%', marginTop: 8, marginBottom: 8 }} value={searchQuery} onChange={setSearchQuery} />
      <Button onClick={exportToExcel} appearance="primary">엑셀 다운로드</Button>
      <DndProvider backend={HTML5Backend}>
        <Table autoHeight data={articles} loading={loading} rowHeight={64}>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.Cell dataKey="id" />
          </Table.Column>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>평수</Table.HeaderCell>
            <Table.Cell dataKey="houseSize" />
          </Table.Column>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>거주형태</Table.HeaderCell>
            <Table.Cell dataKey="housingType" />
          </Table.Column>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>작성자</Table.HeaderCell>
            <Table.Cell dataKey="userId" />
          </Table.Column>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>등록일자</Table.HeaderCell>
            <Table.Cell>
              {
                (rowData) => dayjs(rowData.createdAt).format("YYYY년 MM월 DD일")
              }
            </Table.Cell>
          </Table.Column>
          <Table.Column flexGrow={1.5}>
            <Table.HeaderCell>#</Table.HeaderCell>
            <Table.Cell>
            {
                (rowData) => (
                  <>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        window.open(`http://vanessdeco.com/community/photo/detail.html?id=${rowData.id}`)
                      }}
                    >
                      게시글 보기
                    </Button>
                    <Button
                      style={{ marginLeft: 8 }}
                      appearance="primary"
                      color={rowData.isActive ? 'red' : 'green'}
                      onClick={() => toggleArticle(rowData.id)}
                    >
                      { rowData.isActive ? '비활성화' : '활성화' }
                    </Button>
                    <Button
                      style={{ marginLeft: 8 }}
                      appearance="default"
                      color="red"
                      onClick={() => removeArticle(rowData.id)}
                    >
                      삭제
                    </Button>
                  </>
                )
              }
            </Table.Cell>
          </Table.Column>
          <Table.Column flexGrow={1}>
            <Table.HeaderCell>정렬</Table.HeaderCell>
            <DraggableCell onDrag={handleDragRow}>
              <Icon icon="arrows"/>
            </DraggableCell>
          </Table.Column>
        </Table>
      </DndProvider>
    </Page>
  );
};

export default Picture;